/**
 * Feature Access Zustand Store
 *
 * State management for feature-based access control.
 * Handles user features, admin functions, and access checks.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  getUserFeatures,
  checkFeatureAccess,
  getEnabledFeatureIds,
  checkIsAdmin,
  getAllUsersWithFeatures,
  setFeatureAccess,
  bulkSetFeatureAccess,
  getAllProfiles,
  FEATURES,
  FEATURE_INFO
} from '../services/featureAccessService';
import toast from 'react-hot-toast';

export const useFeatureAccessStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // User's enabled features
    enabledFeatures: [],
    allFeatures: [],

    // Admin state
    isAdmin: false,
    users: [],
    selectedUser: null,

    // Loading states
    isLoading: true,
    isLoadingUsers: false,
    isSaving: false,

    // Initialization tracking
    _initializedUserId: null,
    _isInitializing: false,

    // Modal states
    isUserModalOpen: false,

    // =====================================================
    // ACTIONS: Initialization
    // =====================================================

    /**
     * Initialize feature access for current user
     * Deduplicates calls - won't re-fetch if already initialized for same user
     */
    initialize: async (userId, force = false) => {
      if (!userId) {
        set({ isLoading: false, enabledFeatures: [], isAdmin: false, _initializedUserId: null });
        return;
      }

      const { _initializedUserId, _isInitializing } = get();

      // Skip if already initialized for this user (unless forced)
      if (!force && _initializedUserId === userId) {
        return;
      }

      // Skip if already initializing
      if (_isInitializing) {
        return;
      }

      set({ isLoading: true, _isInitializing: true });

      try {
        // Get user's features and admin status in parallel
        const [features, isAdmin] = await Promise.all([
          getUserFeatures(userId),
          checkIsAdmin(userId)
        ]);

        const enabledFeatures = features
          .filter(f => f.is_enabled)
          .map(f => f.feature_id);

        set({
          allFeatures: features,
          enabledFeatures,
          isAdmin,
          isLoading: false,
          _initializedUserId: userId,
          _isInitializing: false
        });
      } catch (error) {
        console.error('Failed to initialize feature access:', error);
        set({ isLoading: false, _isInitializing: false });
        toast.error('Feature access could not be loaded');
      }
    },

    /**
     * Refresh features for current user
     */
    refresh: async (userId) => {
      if (!userId) return;

      try {
        const features = await getUserFeatures(userId);
        const enabledFeatures = features
          .filter(f => f.is_enabled)
          .map(f => f.feature_id);

        set({ allFeatures: features, enabledFeatures });
      } catch (error) {
        console.error('Failed to refresh features:', error);
      }
    },

    // =====================================================
    // ACTIONS: Access Checks
    // =====================================================

    /**
     * Check if user has access to a feature
     */
    hasAccess: (featureId) => {
      const { enabledFeatures, isLoading } = get();
      if (isLoading) return false;
      return enabledFeatures.includes(featureId);
    },

    /**
     * Get enabled feature count
     */
    getEnabledCount: () => {
      const { enabledFeatures } = get();
      return enabledFeatures.length;
    },

    /**
     * Get feature info by ID
     */
    getFeatureInfo: (featureId) => {
      return FEATURE_INFO[featureId] || null;
    },

    // =====================================================
    // ACTIONS: Admin - User Management
    // =====================================================

    /**
     * Load all users with their features (admin only)
     */
    loadUsers: async () => {
      const { isAdmin } = get();
      if (!isAdmin) return;

      set({ isLoadingUsers: true });

      try {
        const users = await getAllUsersWithFeatures();
        set({ users, isLoadingUsers: false });
      } catch (error) {
        console.error('Failed to load users:', error);
        set({ isLoadingUsers: false });
        toast.error('Users could not be loaded');
      }
    },

    /**
     * Select user for editing
     */
    selectUser: (user) => {
      set({ selectedUser: user, isUserModalOpen: true });
    },

    /**
     * Close user modal
     */
    closeUserModal: () => {
      set({ selectedUser: null, isUserModalOpen: false });
    },

    /**
     * Update feature access for a user (admin only)
     */
    updateUserFeature: async (userId, featureId, enabled, adminId) => {
      const { isAdmin } = get();
      if (!isAdmin) {
        toast.error('Unauthorized');
        return false;
      }

      set({ isSaving: true });

      try {
        await setFeatureAccess(userId, featureId, enabled, adminId);

        // Update local state
        set(state => ({
          users: state.users.map(user => {
            if (user.user_id !== userId) return user;

            const features = typeof user.features === 'string'
              ? JSON.parse(user.features)
              : user.features;

            return {
              ...user,
              features: features.map(f =>
                f.feature_id === featureId
                  ? { ...f, is_enabled: enabled, enabled_at: enabled ? new Date().toISOString() : null }
                  : f
              )
            };
          }),
          isSaving: false
        }));

        toast.success(enabled ? 'Feature enabled' : 'Feature disabled');
        return true;
      } catch (error) {
        console.error('Failed to update feature access:', error);
        set({ isSaving: false });
        toast.error('Update failed');
        return false;
      }
    },

    /**
     * Bulk update features for a user (admin only)
     */
    bulkUpdateUserFeatures: async (userId, featureStates, adminId) => {
      const { isAdmin } = get();
      if (!isAdmin) {
        toast.error('Unauthorized');
        return false;
      }

      set({ isSaving: true });

      try {
        await bulkSetFeatureAccess(userId, featureStates, adminId);
        await get().loadUsers(); // Reload users
        set({ isSaving: false });
        toast.success('Features updated');
        return true;
      } catch (error) {
        console.error('Failed to bulk update features:', error);
        set({ isSaving: false });
        toast.error('Update failed');
        return false;
      }
    },

    // =====================================================
    // ACTIONS: Reset
    // =====================================================

    /**
     * Reset store state
     */
    reset: () => {
      set({
        enabledFeatures: [],
        allFeatures: [],
        isAdmin: false,
        users: [],
        selectedUser: null,
        isLoading: true,
        isLoadingUsers: false,
        isSaving: false,
        isUserModalOpen: false,
        _initializedUserId: null,
        _isInitializing: false
      });
    }
  }))
);

// Export feature constants for convenience
export { FEATURES, FEATURE_INFO };
